// Google Analytics (gtag.js) service.
//
// The measurement ID is provided at container start by
// docker-image-req/inject-env.sh as window.config.GA_MEASUREMENT_ID
// (G-XHC3H86GDY in production, G-90N17SNE46 in dev). For local
// development (`yarn start`, no runtime injection) it falls back to
// REACT_APP_GA_MEASUREMENT_ID from .env. If neither is set, every
// function here is a safe no-op, so analytics can never break the app.

import { getEnvValue } from "./getEnvValue";

const MEASUREMENT_ID = getEnvValue("GA_MEASUREMENT_ID", "")

let initialized = false;

export const isAnalyticsEnabled = () => Boolean(MEASUREMENT_ID);

// gtag must push the `arguments` object (not an array) onto dataLayer —
// this mirrors Google's official snippet.
export function gtag() {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(arguments);
}

/**
 * Load gtag.js and configure it. Call once at app startup
 * (done by <GoogleAnalytics />). Safe to call multiple times.
 */
export function initAnalytics(userId) {
  if (initialized || !isAnalyticsEnabled()) return;
  initialized = true;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  gtag("js", new Date());
}

/**
 * Report an SPA page view. Called by <GoogleAnalytics /> on every
 * React Router location change.
 */
export function trackPageView(path) {
  if (!isAnalyticsEnabled()) return;
  gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

/**
 * Attach the signed-in user to analytics. All subsequent events and
 * page views are associated with this user.
 */
export function setAnalyticsUser({ userId, email, name } = {}) {
  if (!isAnalyticsEnabled() || !email) return;
  gtag("config", MEASUREMENT_ID, { user_id: userId, send_page_view: false });
  gtag("set", { user_id: userId });
  gtag("set", "user_properties", {
    user_email: email,
    ...(name ? { user_name: name } : {}),
  });
}

/** Detach the user (e.g., on logout). */
export function clearAnalyticsUser() {
  if (!isAnalyticsEnabled()) return;
  gtag("set", { user_id: null });
  gtag("set", "user_properties", { user_email: null, user_name: null });
}

/**
 * Track a custom event from anywhere in the app:
 *   import { trackEvent } from "../services/analytics";
 *   trackEvent("message_sent", { conversation_id: id });
 */
export function trackEvent(eventName, params = {}) {
  if (!isAnalyticsEnabled()) return;
  gtag("event", eventName, params);
}
